import React, { useState, useEffect, useMemo } from 'react';
import { Col, Form, Row, Stack } from 'react-bootstrap';
import {
  Button,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Alert,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import Modal from '../../../elements/Modal';
import CustomConfigCreateForm from '../../../configurations/config/CustomConfigCreateForm';
import { checkTechLayerCompatibility } from '../../../../lib/utils';
import { bulkUpdateTestcases } from '../../../../modules/testcases';
import { renderUserOptions } from '../../../../lib/userUtils';
import { useNotification } from '../../../../hooks/NotificationContext';
import MultiSelectDropdown from '../../../elements/MultiSelectDropdown';
import EditModeDropdown, { allowClearForField } from '../../../../static-data/EditModeDropdown';
import { getProjectConfig } from '../../../../modules/executions';
import { formatCountryOptions } from '../../../../static-data/formatCountryOptions';
import { automationStatuses } from '../../../../static-data/DropdownOptions';

const FIELD_OPTIONS = [
  { id: 'priority', name: 'Priority' },
  { id: 'status', name: 'Status' },
  { id: 'labels', name: 'Labels' },
  { id: 'module', name: 'Module' },
  { id: 'technologyLayer', name: 'Technology Layer' },
  { id: 'testSuites', name: 'Test Suites' },
  { id: 'country', name: 'Countries' },
  { id: 'isAutomationNeeded', name: 'Candidate for Automation' },
  { id: 'isAutomated', name: 'Is Automated?' },
  
];

const BulkEditModal = ({
  isOpen,
  onClose,
  selectedTestCases,
  priorities = [],
  statuses = [],
  labels = [],
  modules = [],
  testSuites = [],
  projectUsers = [],
  isReviewWorkflow = false,
  technologyLayers = [],
  fetchTestCases,
  fetchDropdownData,
}) => {
  const [fieldsToEdit, setFieldsToEdit] = useState([]);
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState({});
  const [warning, setWarning] = useState(null);
  const [geographies, setGeographies] = useState([]);
  const [backendWarning, setBackendWarning] = useState(null);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const currentUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'))?.id || null;
    } catch {
      return null;
    }
  }, []);

  const showReviewer = useMemo(() => {
    const selectedStatus = statuses.find((s) => s.id === formData.status);
    return selectedStatus?.name === 'In Review' && isReviewWorkflow;
  }, [formData.status, isReviewWorkflow, statuses]);

  const filteredStatuses = useMemo(() => {
    const user = projectUsers.find((u) => u.id === currentUserId);
    const isTeamMember = user?.role === 'member';
    if (isTeamMember && isReviewWorkflow) {
      return statuses.filter((s) => s.name !== 'Approved');
    }
    return statuses;
  }, [statuses, projectUsers, currentUserId, isReviewWorkflow]);

  useEffect(() => {
    let mounted = true;
    const fetchProjectData = async () => {
      try {
        const response = await getProjectConfig(projectId);
        const countriesArray = response?.geographies || [];
        const formatCountryWithFlag = formatCountryOptions(countriesArray || []);
        const formattedGeographies = formatCountryWithFlag.map((country) => ({
          id: country.value,
          name: country.value,
          display: country.display,
        }));
        if (mounted) setGeographies(formattedGeographies);
      } catch (error) {
        console.error('Error fetching project config:', error);
      }
    };
    fetchProjectData();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  const resetForm = () => {
    setFieldsToEdit([]);
    setFormData({});
    setEditMode({});
    setWarning(null);
    setBackendWarning(null);  
    setPendingSubmission(null); 
  };

  useEffect(() => {
    setFormData((prev) => {
      if (!fieldsToEdit || fieldsToEdit.length === 0) return {};
      const next = { ...prev };
      Object.keys(prev).forEach((k) => {
        if (!fieldsToEdit.includes(k) && !['reviewer'].includes(k)) {
          delete next[k];
        }
      });
      return next;
    });
  }, [fieldsToEdit]);
  useEffect(() => {
   if (backendWarning) {
    setBackendWarning(null);
    setPendingSubmission(null);
   }
  }, [formData.automationCountry,formData.isAutomated]);
  const handleInputChange = (field, value) => {
    const newValue = value === '' ? null : value;

    if (field === 'technologyLayer') {
      const warningText = checkTechLayerCompatibility(formData.testSuites || [], newValue, testSuites);
      setWarning(warningText || null);
    } else if (field === 'testSuites') {
      const newSelectedSuites = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : value;
      const warningText = checkTechLayerCompatibility(newSelectedSuites, formData?.technologyLayer, testSuites);
      setWarning(warningText || null);
    }
    if (field === 'isAutomated' && value === 'Yes') {
      setFormData((prevData) => ({
        ...prevData,
        automatedBy: prevData.automatedBy || currentUserId || projectUsers?.[0]?.id || null,
        automationStatus: prevData.automationStatus || 'Done',
      }));
    }

    if (field === 'isAutomationNeeded' && value === 'Yes') {
      setFormData((prevData) => ({
        ...prevData,
        automationStatus: prevData.automationStatus || automationStatuses[0] || 'Todo',
      }));
    }
    setFormData((prev) => ({ ...prev, [field]: newValue }));
  };
  const handleEditModeChange = (field, mode) => {
    setEditMode((prev) => ({ ...prev, [field]: mode }));
  };
  const handleSubmit = async () => {
    if (!fieldsToEdit.length) return;

    if (fieldsToEdit.includes('isAutomated') && formData.isAutomated === 'Yes') {
      if (!formData.automatedBy || !formData.automationMethod) {
        addNotification('danger', 'Please provide both "Automated By" and "Automation Method".');
        return;
      }
    }

    const submission = {
      testcases: selectedTestCases.map((t) => t.id),
      editMode,
    };

    fieldsToEdit.forEach((field) => {
      let value = formData[field];

      if (field === 'country' && Array.isArray(value)) {
        value = geographies.filter((geo) => value.includes(geo.id)).map((g) => g.name);
      }

      if (field === 'automationCountry' && Array.isArray(value)) {
        value = geographies.filter((geo) => value.includes(geo.id)).map((g) => g.name);
      }

      submission[field] = value;
    });

    if (showReviewer) {
      submission.reviewer = formData.reviewer || null;
    }

    if (fieldsToEdit.includes('isAutomationNeeded')) {
      if (formData.isAutomationNeeded === 'Yes') {
        submission.automationStatus = formData.automationStatus || automationStatuses[0] || 'Todo';
        submission.isAutomationNeeded = 'Yes';
      } else {
        submission.isAutomationNeeded = 'No';
        submission.automationStatus = null;
      }
    }

    if (fieldsToEdit.includes('isAutomated')) {
      if (formData.isAutomated === 'Yes') {
        submission.automatedBy = formData.automatedBy || currentUserId || projectUsers?.[0]?.id || null;
        submission.automationMethod = formData.automationMethod || '';
        submission.automationStatus = 'Done';
        submission.automationCountry = geographies
          .filter((g) => formData.automationCountry?.includes(g.id))
          .map((g) => g.name);
      } else {
        submission.automatedBy = null;
        submission.automationMethod = null;
        submission.automationStatus = null;
        submission.automationCountry = [];
      }
    }

    try {
      const res = await bulkUpdateTestcases(submission);
      if (res.warning && res.requires_confirmation) {
        console.log('Backend warning:', res.message);
        setBackendWarning(res.message);
        setPendingSubmission(submission);
        return;
      }

      addNotification('success', 'Test cases updated successfully!');
      fetchTestCases();
      handleClose();
    } catch (err) {
      addNotification('danger', err.message || 'Failed to update test cases. Please try again later.');
    }
  };

  const handleClose = () => {
    resetForm();
    onClose(false);
  };

  const renderSelect = ({ field, options, isMulti = false }) => {
    const rawValue = formData[field];
    const value = isMulti ? (Array.isArray(rawValue) ? rawValue : []) : (rawValue ?? '');

    return (
      <FormControl fullWidth size="small">
        <Select
          multiple={isMulti}
          displayEmpty
          value={value}
          onChange={(e) => {
            handleInputChange(field, e.target.value);
          }}
          renderValue={(selected) =>
            isMulti
              ? options
                .filter((opt) => selected.includes(opt.id))
                .map((opt) => opt.name)
                .join(', ')
              : (options.find((opt) => opt.id === value)?.name || 'None')
          }
        >
          {!options.length ? (
            <MenuItem disabled>No {field} found</MenuItem>
          ) : (
            options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {isMulti && <Checkbox checked={value.includes(opt.id)} />}
                <ListItemText
                  primary={(field === 'country' || field === 'automationCountry') && opt.display ? opt.display : opt.name}
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    );
  };

  const renderFieldInput = (field) => {
    switch (field) {
      case 'priority':
        return renderSelect({ field, options: priorities });
      case 'status': {
        const selectedStatus = filteredStatuses.find((s) => s.id === formData.status);
        return (
          <>
            {renderSelect({ field, options: filteredStatuses })}
            {selectedStatus?.name === 'Archived' && (
              <Typography variant="caption" color="error">
                * Archiving a test case will remove it from all test suites. It cannot be linked to test cycles or executed.
              </Typography>
            )}
          </>
        );
      }
      case 'module':
        return renderSelect({ field, options: modules });
      case 'technologyLayer':
        return renderSelect({
          field,
          options: technologyLayers.map((layer) => ({ id: layer.name, name: layer.name })),
        });
      case 'labels':
      case 'testSuites':
      case 'country': {
        const options = field === 'labels' ? labels : field === 'testSuites' ? testSuites : geographies;
        return (
          <>
            <Stack direction="horizontal" gap={2} className="align-items-start">
              {renderSelect({ field, options, isMulti: true })}
              <EditModeDropdown
                field={field}
                value={editMode[field] || 'append'}
                onChange={handleEditModeChange}
                allowClear={allowClearForField(field)}
              />
            </Stack>
            {field === 'labels' && (
              <CustomConfigCreateForm configType="labels" fetchDropdownData={fetchDropdownData} />
            )}
          </>
        );
      }

      case 'isAutomated':
        return (
          <>
            <Form.Select
              value={formData.isAutomated || ''}
              onChange={(e) => handleInputChange('isAutomated', e.target.value)}
              className="form-input"
            >
              <option value="" disabled>Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Form.Select>

            {formData.isAutomated === 'Yes' && (
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Label>Automated By</Form.Label>
                  <Form.Select
                    value={formData.automatedBy || ''}
                    onChange={(e) => handleInputChange('automatedBy', e.target.value)}
                    className="form-input"
                  >
                    <option value="" disabled>None</option>
                    {renderUserOptions(projectUsers)}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Automation Method</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.automationMethod || ''}
                    onChange={(e) => handleInputChange('automationMethod', e.target.value)}
                    placeholder="Enter automation method"
                    maxLength={100}
                  />
                </Col>

                <Col xs={12} className="mt-3">
                  <Form.Label>Automated For Countries</Form.Label>
                  {renderSelect({ field: 'automationCountry', options: geographies, isMulti: true })}
                </Col>
              </Row>
            )}
          </>
        );

      case 'isAutomationNeeded':
        return (
          <>
            <Form.Select
              value={formData.isAutomationNeeded || ''}
              onChange={(e) => handleInputChange('isAutomationNeeded', e.target.value)}
              className="form-input"
            >
              <option value="" disabled>Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Form.Select>

            {formData.isAutomationNeeded === 'Yes' && (
              <div className="mt-3">
                <Form.Label>Automation Status</Form.Label>
                <Form.Select
                  value={formData.automationStatus || ''}
                  onChange={(e) => handleInputChange('automationStatus', e.target.value)}
                  className="form-input"
                >
                  <option value="" disabled>Select</option>
                  {automationStatuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={isOpen}
      handleClose={handleClose}
      modalTitle="Bulk Edit Test Cases"
      PaperProps={{
       sx: {
        height: "500px",
        maxHeight: "500px",     
       }
      }}
      action={() => (
        <>
          <Button variant="outlined" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !fieldsToEdit.length ||
              !selectedTestCases.length ||
              (fieldsToEdit.includes('status') && !formData.status) ||
              (fieldsToEdit.includes('status') && showReviewer && !formData.reviewer) ||
              (fieldsToEdit.includes('testSuites') && formData?.testSuites?.length === 0) ||
              (fieldsToEdit.includes('technologyLayer') && !formData.technologyLayer) ||
              (fieldsToEdit.includes('priority') && !formData.priority) ||
              (fieldsToEdit.includes('isAutomated') && !formData.isAutomated) ||
              (fieldsToEdit.includes('isAutomated') && formData.isAutomated === 'Yes' && (!formData.automatedBy || !formData.automationMethod)) ||
              (fieldsToEdit.includes('isAutomationNeeded') && formData.isAutomationNeeded === 'Yes' && !formData.automationStatus) || backendWarning || pendingSubmission
            }
          >
            Apply
          </Button>
        </>
      )}
    >
      {!selectedTestCases.length ? (
        <Typography color="error" className="text-center py-4">
          Please select test cases to bulk edit.
        </Typography>
      ) : (
        <>
        <Alert severity="info" className="mb-2">
          Selecting <strong>Is Automated</strong> will require providing additional details such as 
          <strong> Automated By</strong>, <strong>Automation Method</strong>, and 
          <strong> Automation Country</strong>.
          <br />
          Enabling <strong>Candidate for Automation</strong> will allow you to specify the
          <strong> Candidate Automation Details</strong>.
        </Alert>
          <div className="mb-3">
            <Form.Label>Selected Test Cases</Form.Label>
            <div className="border rounded bg-light p-2">
              {selectedTestCases.map(({ key }) => key).join(', ')}
            </div>
          </div>

          <div className="mb-3">
            <Form.Label>Select Fields to Edit</Form.Label>
            <MultiSelectDropdown
              options={FIELD_OPTIONS}
              selectedValues={fieldsToEdit}
              onChange={setFieldsToEdit}
              placeholder="Select fields"
              showSelectAll={false}
            />
          </div>

          {warning && (
            <Alert severity="warning" className="my-2">{warning}</Alert>
          )}
          <Row>
            {fieldsToEdit.map((field) => {
              const label = FIELD_OPTIONS.find((opt) => opt.id === field)?.name;
              const isMulti = field === 'labels' || field === 'testSuites' || field === 'country';

              return (
                <Col key={field} xs={12} md={isMulti ? 12 : 6} className="mb-3">
                  <Form.Label>{label}</Form.Label>
                  {renderFieldInput(field)}

                  {field === 'status' && showReviewer && (
                    <div className="mt-3">
                      <Form.Label>Reviewer</Form.Label>
                      <Form.Select
                        value={formData.reviewer || ''}
                        onChange={(e) => handleInputChange('reviewer', e.target.value)}
                        className="form-input"
                      >
                        <option value="" disabled>None</option>
                        {renderUserOptions(projectUsers)}
                      </Form.Select>
                    </div>
                  )}
                </Col>
              );
            })}
          </Row>
          {backendWarning && (
            <Alert
              severity="warning"
              className="my-2"
              action={
                <Button
                  color="warning"
                  variant="contained"
                  size="small"
                  onClick={async () => {
                    try {
                      const forced = { ...pendingSubmission, forceAddCountry: true };
                      await bulkUpdateTestcases(forced);

                      addNotification('success', 'Missing countries added and test cases updated!');
                      setBackendWarning(null);
                      setPendingSubmission(null);
                      fetchTestCases();
                      handleClose();
                    } catch (err) {
                      addNotification('danger', err.message || 'Update failed.');
                    }
                  }}
                >
                  Add 
                </Button>
              }
            >
              {backendWarning}
            </Alert>
          )}
        </>
      )}
    </Modal>
  );
};

export default BulkEditModal;
